import React, { useEffect, useState, useRef, useCallback } from 'react';
import { NewsfilateProHeader } from '../components/NewsfilateProHeader';
import { NewsfilateFooter } from '../components/NewsfilateFooter';
import { CheckIcon, XIcon, SearchIcon, SparklesIcon, BarChartIcon, ChevronDownIcon, ZapIcon } from 'lucide-react';
import { getBaseUrl } from '../utils/api';
export function NewsfilateProPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [cognitoId, setCognitoId] = useState<string | null>(null);
  const [userActivities, setUserActivities] = useState<string[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [hasSignedUpViaEmail, setHasSignedUpViaEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailDialogRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Check localStorage for email signup on mount
  useEffect(() => {
    const emailSignupStatus = localStorage.getItem('newsfiliate_pro_beta_join_email');
    if (emailSignupStatus === 'true') {
      setHasSignedUpViaEmail(true);
    }
  }, []);

  // Function to fetch user data
  const fetchUserData = useCallback(async (id: string) => {
    setIsLoadingUser(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user?cognitoId=${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.user) {
          setUserName(data.user.name);
          setUserActivities(data.user.activity || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  // Fetch user data on page load if id parameter exists
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setCognitoId(id);
      fetchUserData(id);
    }
  }, [fetchUserData]);
  // Handle email dialog outside clicks and ESC key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emailDialogRef.current && !emailDialogRef.current.contains(event.target as Node)) {
        setShowEmailDialog(false);
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowEmailDialog(false);
      }
    };
    if (showEmailDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'auto';
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [showEmailDialog]);

  // Reset email input when dialog closes
  useEffect(() => {
    if (!showEmailDialog) {
      setEmailInput('');
    }
  }, [showEmailDialog]);

  // Set video playback speed to 2x
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 2.0;
    }
  }, []);
  const handleJoinBetaClick = async (e?: React.MouseEvent) => {
    e?.preventDefault();

    // If user is known (has cognitoId and userName), POST activity directly
    if (cognitoId && userName) {
      setIsSubmitting(true);
      try {
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}user/activity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            activity_name: 'newsfiliate_pro_beta_join',
            cognito_id: cognitoId
          })
        });
        if (response.ok) {
          console.log('Activity logged successfully');
          // Re-fetch user data to update activities
          if (cognitoId) {
            await fetchUserData(cognitoId);
          }
        } else {
          console.error('Failed to log activity');
        }
      } catch (error) {
        console.error('Error logging activity:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // User not known - show email dialog
      setShowEmailDialog(true);
    }
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput) return;
    setIsSubmitting(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activity_name: 'newsfiliate_pro_beta_join',
          cognito_id: '1',
          activity_data: {
            email: emailInput
          }
        })
      });
      if (response.ok) {
        // Save signup status to localStorage
        localStorage.setItem('newsfiliate_pro_beta_join_email', 'true');
        setHasSignedUpViaEmail(true);
        setShowEmailDialog(false);
        setEmailInput('');
        console.log('Email submitted successfully');
      } else {
        console.error('Failed to submit email');
      }
    } catch (error) {
      console.error('Error submitting email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Extract first name from full name
  const firstName = userName ? userName.split(' ')[0] : null;

  // Check if user has already signed up for beta (via API or email)
  const isSignedUpForBeta = userActivities.includes('newsfiliate_pro_beta_join') || hasSignedUpViaEmail;
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <div className="flex flex-col min-h-screen bg-white font-sans" data-id="element-1496">
      <NewsfilateProHeader onJoinBeta={handleJoinBetaClick} isSignedUp={isSignedUpForBeta} data-id="element-1497" />

      <main className="flex-grow" data-id="element-1498">
        {/* Hero Section */}
        <section className="pt-20 pb-16 md:pt-32 md:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-[#f2f1f8]/30" data-id="element-1499">
          <div className="max-w-7xl mx-auto text-center" data-id="element-1500">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f2f1f8] text-[#5e60ce] text-sm font-medium mb-8" data-id="element-1501">
              <span className="relative flex h-2 w-2" data-id="element-1502">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5e60ce] opacity-75" data-id="element-1503"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#5e60ce]" data-id="element-1504"></span>
              </span>
              New: Newsfiliate Pro Beta
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight max-w-5xl mx-auto leading-tight" data-id="element-1505">
              Turn your newsletter into an{' '}
              <span className="text-[#5e60ce]" data-id="element-1506">affiliate revenue engine</span>
            </h1>

            <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-1507">
              Newsfiliate Pro is a browser extension that scans your Kit email
              draft as you write and gives you one-click affiliate product and
              copy suggestions in your voice so monetizing doesn't become "one
              more thing" and you stay consistent without breaking flow.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8" data-id="element-1508">
              {isSignedUpForBeta ? <div className="px-8 py-4 bg-green-50 text-green-700 border border-green-200 rounded-lg font-medium flex items-center" data-id="element-1509">
                  <CheckIcon className="w-5 h-5 mr-2" data-id="element-1510" />
                  You're on the Beta list!
                </div> : <button onClick={handleJoinBetaClick} className="w-full sm:w-auto px-8 py-4 bg-[#5e60ce] text-white text-lg font-semibold rounded-lg hover:bg-[#4a4db0] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-1511">
                  {firstName ? `${firstName}, Get Early Access (Join Beta)` : 'Get Early Access (Join Beta)'}
                </button>}

              <button onClick={() => scrollToSection('pricing')} className="w-full sm:w-auto px-8 py-4 bg-white text-gray-700 border border-gray-300 text-lg font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1512">
                See Pro vs Pro+
              </button>
            </div>

            <p className="text-sm text-gray-500 font-medium" data-id="element-1513">
              Beta members get early access and a 30-day free trial.
            </p>

            {/* Product Mock Placeholder */}
            <div className="mt-16 relative mx-auto max-w-5xl rounded-xl shadow-2xl border border-gray-200 overflow-hidden bg-white aspect-[16/9] p-4" data-id="element-1514">
              <video ref={videoRef} className="w-full h-full object-contain rounded-lg" autoPlay muted loop playsInline onLoadedMetadata={e => {
              e.currentTarget.playbackRate = 2.0;
            }} data-id="element-1515">
                <source src="https://s3.us-east-1.amazonaws.com/cdn.getpoln.com/creatorboost/newsfiliatepro/NF_Pro_Suggestions.mp4" type="video/mp4" data-id="element-1516" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        {/* Instant Clarity Strip */}
        <section className="py-12 bg-white border-b border-gray-100" data-id="element-1517">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1518">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-100" data-id="element-1519">
              <div className="text-center px-4 pt-4 md:pt-0" data-id="element-1520">
                <p className="text-sm font-bold text-[#5e60ce] uppercase tracking-wider mb-2" data-id="element-1521">
                  What it is
                </p>
                <p className="text-gray-900 font-medium" data-id="element-1522">
                  A Kit editor extension for affiliate linking + suggestions
                  based on your data
                </p>
              </div>
              <div className="text-center px-4 pt-8 md:pt-0" data-id="element-1523">
                <p className="text-sm font-bold text-[#5e60ce] uppercase tracking-wider mb-2" data-id="element-1524">
                  Who it's for
                </p>
                <p className="text-gray-900 font-medium" data-id="element-1525">
                  Newsletter creators who want consistent affiliate income
                  without extra workflow
                </p>
              </div>
              <div className="text-center px-4 pt-8 md:pt-0" data-id="element-1526">
                <p className="text-sm font-bold text-[#5e60ce] uppercase tracking-wider mb-2" data-id="element-1527">
                  What you get
                </p>
                <p className="text-gray-900 font-medium" data-id="element-1528">
                  More monetized issues, faster, with better product fit
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 bg-gray-50" data-id="element-1529">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1530">
            <div className="text-center mb-12" data-id="element-1531">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1532">
                Affiliate marketing works… until it becomes "one more thing."
              </h2>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100" data-id="element-1533">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1534">
                <div className="space-y-6" data-id="element-1535">
                  <div className="flex items-start" data-id="element-1536">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1537">
                      <XIcon className="w-5 h-5 text-red-600" data-id="element-1538" />
                    </div>
                    <div className="ml-4" data-id="element-1539">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-1540">
                        Finding products takes time
                      </h3>
                      <p className="text-gray-600 mt-1" data-id="element-1541">
                        Hours spent browsing Amazon instead of writing.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1542">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1543">
                      <XIcon className="w-5 h-5 text-red-600" data-id="element-1544" />
                    </div>
                    <div className="ml-4" data-id="element-1545">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-1546">
                        Inserting links breaks flow
                      </h3>
                      <p className="text-gray-600 mt-1" data-id="element-1547">
                        Tab switching kills your creative momentum.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6" data-id="element-1548">
                  <div className="flex items-start" data-id="element-1549">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1550">
                      <XIcon className="w-5 h-5 text-red-600" data-id="element-1551" />
                    </div>
                    <div className="ml-4" data-id="element-1552">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-1553">
                        Your data sits unused
                      </h3>
                      <p className="text-gray-600 mt-1" data-id="element-1554">
                        No insights on what your audience actually buys.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1555">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1556">
                      <XIcon className="w-5 h-5 text-red-600" data-id="element-1557" />
                    </div>
                    <div className="ml-4" data-id="element-1558">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-1559">
                        Consistency drops
                      </h3>
                      <p className="text-gray-600 mt-1" data-id="element-1560">
                        You give up before revenue becomes meaningful.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-gray-100 text-center" data-id="element-1561">
                <p className="text-xl font-medium text-gray-900" data-id="element-1562">
                  You stop right before affiliate revenue becomes meaningful.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 bg-white" data-id="element-1563">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1564">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1565">
              Meet Newsfiliate Pro: intelligent affiliate monetization inside
              your Kit editor.
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-1566">
              Write as usual → insert products & copy suggestions inside Kit
              email draft → stay in control.
            </p>

            <div className="bg-[#f2f1f8] rounded-xl p-8 inline-block" data-id="element-1567">
              <p className="text-lg font-semibold text-[#5e60ce] flex items-center justify-center gap-2" data-id="element-1568">
                <ZapIcon className="w-5 h-5" data-id="element-1569" />
                Consistent placements with less effort → more clicks, more
                conversions, more predictable affiliate revenue.
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white" data-id="element-1570">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1571">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1572">
              {/* Card 1 */}
              <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-1573">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-6 text-blue-600" data-id="element-1574">
                  <SearchIcon className="w-6 h-6" data-id="element-1575" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1576">
                  Search & Insert
                </h3>
                <ul className="space-y-3" data-id="element-1577">
                  <li className="flex items-start" data-id="element-1578">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1579" />
                    <span className="text-gray-600" data-id="element-1580">
                      Search Amazon inside Kit (no tab switching)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1581">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1582" />
                    <span className="text-gray-600" data-id="element-1583">
                      One-click link or affiliate product box insert into draft
                    </span>
                  </li>
                </ul>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-1584">
                <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center mb-6 text-purple-600" data-id="element-1585">
                  <SparklesIcon className="w-6 h-6" data-id="element-1586" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1587">
                  Contextual Suggestions
                </h3>
                <ul className="space-y-3" data-id="element-1588">
                  <li className="flex items-start" data-id="element-1589">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1590" />
                    <span className="text-gray-600" data-id="element-1591">
                      Scans your draft as you write it for context
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1592">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1593" />
                    <span className="text-gray-600" data-id="element-1594">
                      Recommends products that fit organically
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1595">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1596" />
                    <span className="text-gray-600" data-id="element-1597">
                      Suggests copy in your voice more likely to convert
                    </span>
                  </li>
                </ul>
              </div>

              {/* Card 3 */}
              <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-1598">
                <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mb-6 text-indigo-600" data-id="element-1599">
                  <BarChartIcon className="w-6 h-6" data-id="element-1600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1601">
                  Data-Informed Optimization
                </h3>
                <ul className="space-y-3" data-id="element-1602">
                  <li className="flex items-start" data-id="element-1603">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1604" />
                    <span className="text-gray-600" data-id="element-1605">
                      Uses Kit history signals: links, tags, clicks
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1606">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1607" />
                    <span className="text-gray-600" data-id="element-1608">
                      Helps choose products and phrasing aligned to your
                      audience
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1609">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1610" />
                    <span className="text-gray-600" data-id="element-1611">
                      Let your data do the thinking: instant guidance on what to
                      promote and what to say
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Before vs After */}
        <section className="py-20 bg-gray-50" data-id="element-1612">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1613">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-1614">
              The Difference is Flow
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1615">
              <div className="bg-white p-8 rounded-xl border border-red-100 shadow-sm relative overflow-hidden" data-id="element-1616">
                <div className="absolute top-0 left-0 w-full h-1 bg-red-400" data-id="element-1617"></div>
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1618">
                  <XIcon className="w-6 h-6 text-red-500 mr-2" data-id="element-1619" />
                  Before
                </h3>
                <div className="space-y-4" data-id="element-1620">
                  <div className="flex items-center text-gray-600" data-id="element-1621">
                    <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold mr-3" data-id="element-1622">
                      1
                    </span>
                    Write content
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-1623">
                    <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold mr-3" data-id="element-1624">
                      2
                    </span>
                    Finish draft
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-1625">
                    <span className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center text-sm font-bold mr-3 text-red-500" data-id="element-1626">
                      3
                    </span>
                    Dread adding links
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-1627">
                    <span className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center text-sm font-bold mr-3 text-red-500" data-id="element-1628">
                      4
                    </span>
                    Tab switch & lose momentum
                  </div>
                  <div className="flex items-center text-gray-400 italic" data-id="element-1629">
                    <span className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-sm font-bold mr-3" data-id="element-1630">
                      5
                    </span>
                    Skip monetization
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl border border-green-100 shadow-lg relative overflow-hidden transform md:-translate-y-4" data-id="element-1631">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#5e60ce]" data-id="element-1632"></div>
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1633">
                  <CheckIcon className="w-6 h-6 text-[#5e60ce] mr-2" data-id="element-1634" />
                  After (With Pro)
                </h3>
                <div className="space-y-4" data-id="element-1635">
                  <div className="flex items-center text-gray-900 font-medium" data-id="element-1636">
                    <span className="w-8 h-8 rounded-full bg-[#f2f1f8] flex items-center justify-center text-sm font-bold mr-3 text-[#5e60ce]" data-id="element-1637">
                      1
                    </span>
                    Write content
                  </div>
                  <div className="flex items-center text-gray-900 font-medium" data-id="element-1638">
                    <span className="w-8 h-8 rounded-full bg-[#f2f1f8] flex items-center justify-center text-sm font-bold mr-3 text-[#5e60ce]" data-id="element-1639">
                      2
                    </span>
                    Insert products while writing
                  </div>
                  <div className="flex items-center text-gray-900 font-medium" data-id="element-1640">
                    <span className="w-8 h-8 rounded-full bg-[#f2f1f8] flex items-center justify-center text-sm font-bold mr-3 text-[#5e60ce]" data-id="element-1641">
                      3
                    </span>
                    Accept copy suggestions
                  </div>
                  <div className="flex items-center text-gray-900 font-medium" data-id="element-1642">
                    <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-sm font-bold mr-3 text-green-600" data-id="element-1643">
                      4
                    </span>
                    Send & Earn
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-white" data-id="element-1644">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1645">
            <div className="text-center mb-16" data-id="element-1646">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1647">
                Choose Your Plan
              </h2>
              <p className="text-xl text-gray-600" data-id="element-1648">
                Start monetizing smarter today.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1649">
              {/* Plan 1: Pro */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm flex flex-col" data-id="element-1650">
                <div className="mb-6" data-id="element-1651">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1652">
                    Newsfiliate Pro
                  </h3>
                  <p className="text-gray-600" data-id="element-1653">
                    For creators who want to monetize faster while they write.
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow" data-id="element-1654">
                  <li className="flex items-start" data-id="element-1655">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1656" />
                    <span className="text-gray-700" data-id="element-1657">Kit browser extension</span>
                  </li>
                  <li className="flex items-start" data-id="element-1658">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1659" />
                    <span className="text-gray-700" data-id="element-1660">
                      Search & insert Amazon products
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1661">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1662" />
                    <span className="text-gray-700" data-id="element-1663">
                      Contextual recommendations
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1664">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1665" />
                    <span className="text-gray-700" data-id="element-1666">
                      Conversion copy suggestions
                    </span>
                  </li>
                </ul>

                <button onClick={handleJoinBetaClick} className="w-full py-4 border border-[#5e60ce] text-[#5e60ce] font-bold rounded-xl hover:bg-[#f2f1f8] transition-colors" data-id="element-1667">
                  {isSignedUpForBeta ? firstName ? `${firstName}, You're on the list` : "You're on the list" : firstName ? `${firstName}, Join Beta for Pro` : 'Join Beta for Pro'}
                </button>
              </div>

              {/* Plan 2: Pro+ */}
              <div className="bg-white rounded-2xl border-2 border-[#5e60ce] p-8 shadow-xl flex flex-col relative transform md:-translate-y-4" data-id="element-1668">
                <div className="absolute top-0 right-0 bg-[#5e60ce] text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-lg" data-id="element-1669">
                  MOST HANDS-OFF
                </div>

                <div className="mb-6" data-id="element-1670">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1671">
                    Newsfiliate Pro+
                  </h3>
                  <p className="text-gray-600" data-id="element-1672">
                    For creators who want weekly affiliate monetization done for
                    them.
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow" data-id="element-1673">
                  <li className="flex items-start font-medium text-[#5e60ce]" data-id="element-1674">
                    <CheckIcon className="w-5 h-5 mr-3 mt-0.5" data-id="element-1675" />
                    <span data-id="element-1676">Everything in Pro, PLUS:</span>
                  </li>
                  <li className="flex items-start" data-id="element-1677">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1678" />
                    <span className="text-gray-700" data-id="element-1679">
                      Weekly affiliate-centric newsletter drafts
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1680">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1681" />
                    <span className="text-gray-700" data-id="element-1682">
                      Written in your unique voice
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1683">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1684" />
                    <span className="text-gray-700" data-id="element-1685">
                      Repurposes existing content
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1686">
                    <CheckIcon className="w-5 h-5 text-[#5e60ce] mr-3 mt-0.5" data-id="element-1687" />
                    <span className="text-gray-700" data-id="element-1688">
                      Recommends higher-converting alternatives
                    </span>
                  </li>
                </ul>

                <button onClick={handleJoinBetaClick} className="w-full py-4 bg-[#5e60ce] text-white font-bold rounded-xl hover:bg-[#4a4db0] transition-colors shadow-lg" data-id="element-1689">
                  {isSignedUpForBeta ? firstName ? `${firstName}, you're on the list` : "You're on the list" : firstName ? `${firstName}, Apply for Pro+` : 'Apply for Pro+'}
                </button>
                <p className="text-center text-xs text-gray-500 mt-3" data-id="element-1690">
                  Limited spots during beta
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Reversal */}
        <section className="py-16 bg-[#f2f1f8]" data-id="element-1691">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1692">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8" data-id="element-1693">
              Try it without pressure.
            </h2>
            <div className="flex flex-wrap justify-center gap-4 md:gap-12 mb-8" data-id="element-1694">
              <div className="flex items-center text-gray-700 font-medium" data-id="element-1695">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-1696" />
                Early access
              </div>
              <div className="flex items-center text-gray-700 font-medium" data-id="element-1697">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-1698" />
                30-day free trial
              </div>
            </div>
            <button onClick={handleJoinBetaClick} className="px-8 py-3 bg-white border border-gray-300 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1699">
              {isSignedUpForBeta ? firstName ? `${firstName}, you're on the list` : "You're on the list" : firstName ? `${firstName}, Get Early Access` : 'Get Early Access'}
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white" data-id="element-1700">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1701">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-1702">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4" data-id="element-1703">
              {[{
              q: 'Will this change my writing style?',
              a: 'No. Newsfiliate Pro is designed to adapt to your voice. The suggestions are optional, and the Pro+ drafts are trained on your past issues to sound exactly like you.'
            }, {
              q: 'Do I need to be good at affiliate marketing?',
              a: 'Not at all. The biggest win in affiliate marketing is consistency and product fit. We handle the finding and fitting, so you just need to keep creating content.'
            }, {
              q: 'Is this generic advice?',
              a: "No. We use your specific audience signals (clicks, tags, history) to make recommendations. It's tailored to what YOUR readers actually engage with."
            }, {
              q: "What's the difference between Pro and Pro+?",
              a: 'Pro is a tool that lives inside your editor to help you write faster. Pro+ is a service where we generate complete, ready-to-edit drafts for you every week.'
            }].map((item, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1704">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 flex justify-between items-center transition-colors" data-id="element-1705">
                    <span className="font-semibold text-gray-900" data-id="element-1706">
                      {item.q}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`} data-id="element-1707" />
                  </button>
                  {openFaqIndex === index && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-gray-600" data-id="element-1708">
                      {item.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-[#5e60ce] text-white text-center" data-id="element-1709">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1710">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1711">
              Stop treating affiliate monetization like extra work.
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-1712">
              Turn each issue into a compounding revenue asset without leaving
              Kit.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1713">
              <button onClick={handleJoinBetaClick} className="px-8 py-4 bg-white text-[#5e60ce] text-lg font-bold rounded-lg hover:bg-blue-50 transition-colors shadow-lg" data-id="element-1714">
                {isSignedUpForBeta ? firstName ? `${firstName}, you're on the list` : "You're on the list" : firstName ? `${firstName}, Join the Beta` : 'Join the Beta'}
              </button>
              <button onClick={handleJoinBetaClick} className="px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-bold rounded-lg hover:bg-white/10 transition-colors" data-id="element-1715">
                Apply for Pro+
              </button>
            </div>
          </div>
        </section>
      </main>

      <NewsfilateFooter data-id="element-1716" />

      {/* Sticky Mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-40" data-id="element-1717">
        <button onClick={handleJoinBetaClick} className="w-full py-3 bg-[#5e60ce] text-white font-bold rounded-lg shadow-md" data-id="element-1718">
          {isSignedUpForBeta ? firstName ? `${firstName}, you're on the list` : "You're on the list" : firstName ? `${firstName}, Join Beta` : 'Join Beta'}
        </button>
      </div>

    {/* Email Dialog */}
    {showEmailDialog && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1719">
      <div ref={emailDialogRef} className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden" data-id="element-1720">
        <div className="flex justify-end p-4" data-id="element-1721">
          <button onClick={() => setShowEmailDialog(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1722">
              <XIcon className="w-6 h-6" data-id="element-1723" />
            </button>
              </div>
        <div className="px-6 pb-6 pt-2" data-id="element-1724">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-1725">
                Join the Beta
          </h2>
          <p className="text-gray-600 text-center mb-6" data-id="element-1726">
            Enter your email to join the beta program
          </p>
          <form className="space-y-4" onSubmit={handleEmailSubmit} data-id="element-1727">
            <div data-id="element-1728">
              <input type="email" placeholder="Your email address" required value={emailInput} onChange={e => setEmailInput(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-1729" />
            </div>
            <button type="submit" className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-md hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-1730">
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
                </div>
          </div>
        </div>}
    </div>;
}